import type { ReactNode } from "react";

// Inline banners (success / error / warning / info). Shape mirrors
// `ErrorAlert` — `rounded-card`, soft tinted background, semibold heading
// + body. Used for "saved!", "test successful", etc.
type Tone = "success" | "error" | "warning" | "info";

const toneClasses: Record<Tone, string> = {
  success: "border-green-200 bg-green-50 text-green-800",
  error: "border-red-200 bg-red-50 text-red-800",
  warning: "border-yellow-200 bg-yellow-50 text-yellow-800",
  info: "border-blue-light bg-blue-light/40 text-blue-dark",
};

type Props = {
  tone: Tone;
  title?: string;
  children: ReactNode;
  onDismiss?: () => void;
  className?: string;
};

export function Banner({ tone, title, children, onDismiss, className = "" }: Props) {
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={`flex items-start gap-3 rounded-card border p-4 text-sm ${toneClasses[tone]} ${className}`.trim()}
    >
      <div className="flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        <div className={title ? "mt-1" : ""}>{children}</div>
      </div>
      {onDismiss ? (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="rounded-full p-1 text-current opacity-70 transition hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/40"
        >
          <span aria-hidden="true">{"✕"}</span>
        </button>
      ) : null}
    </div>
  );
}
